import React from 'react'
import * as CFRichTextTypes from '@contentful/rich-text-types'
import { Typography } from '@grammarly/badger-ui'
import { FormattedMessage } from 'react-intl'

import { TypeComponentLink, TypeItemTestimonial } from '../../../lib/types'
import { ContentProps } from '../../renderer'
import { renderRichTextSingleLineContent, renderRichTextMultiLineContent } from '../../renderer/rich-text'
import { Image } from '../../ui-lib/image'
import { CtaArrow } from '../../ui-lib/icons/cta-arrow'
import { useIsSectionThemeDark } from '../../../contexts/section-theme-context'
import { updateAssetCDNUrl } from '../../asset/utils'

import styles from './testimonial.module.scss'
import typographyStyles from '../../ui-lib/typography/typography.module.scss'

const { HXL, TXL, C1, C2, C3, C4, C5 } = Typography

interface TypeItemDefaultTestimonialProps {
  personName: string
  jobPosition?: string
  companyName?: string
  asset?: string
  testimonial: CFRichTextTypes.Document
}

interface TypeItemSocialTestimonialProps {
  personName: string
  socialUsername?: string
  asset: string
  testimonial: CFRichTextTypes.Document
}

interface TypeItemStatsTestimonialProps {
  companyLogoSrc: string
  companyStatistics?: CFRichTextTypes.Document
  testimonial: CFRichTextTypes.Document
  personName: string
  jobPosition?: string
  asset: string
  link?: TypeComponentLink
}

const DefaultTestimonialItem: React.FC<TypeItemDefaultTestimonialProps> = ({
  asset,
  testimonial,
  companyName,
  jobPosition,
  personName,
}) => {
  const isDarkTheme = useIsSectionThemeDark()

  return (
    <div className={styles.container}>
      <C1 className={styles.quote} as="p">
        {
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          '“'
        }
        {renderRichTextSingleLineContent(testimonial)}
        {
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          '”'
        }
      </C1>
      <div className={styles.profile}>
        {asset && <Image className={styles.photo} alt="" width={64} height={64} src={asset} />}
        <div>
          <C3 as="div" bold>
            {personName}
          </C3>
          {(jobPosition || companyName) && (
            <C3 className={isDarkTheme ? typographyStyles.colorNeutral10 : typographyStyles.colorNeutral60} as="div">
              {jobPosition && companyName ? (
                <FormattedMessage
                  id="testimonial.jobPosition"
                  description="Testimonial - Job Position"
                  defaultMessage="{jobPosition} at {companyName}"
                  values={{ jobPosition, companyName }}
                />
              ) : (
                <>{jobPosition || companyName}</>
              )}
            </C3>
          )}
        </div>
      </div>
    </div>
  )
}

const SocialTestimonialItem: React.FC<TypeItemSocialTestimonialProps> = ({
  asset,
  testimonial,
  personName,
  socialUsername,
}) => {
  return (
    <div className={styles.socialCard}>
      <div className={styles.socialProfile}>
        {asset && <Image className={styles.socialPhoto} alt="" width={40} height={40} src={asset} />}
        <div className={styles.socialUserInfo}>
          <C4 as="div" bold className={styles.socialFullName}>
            {personName}
          </C4>
          <C5 as="div" className={styles.socialUsername}>
            {socialUsername}
          </C5>
        </div>
      </div>
      <C4 className={styles.socialQuote} as="p">
        {renderRichTextMultiLineContent(testimonial)}
      </C4>
    </div>
  )
}

const StatsTestimonialItem: React.FC<TypeItemStatsTestimonialProps> = ({
  companyLogoSrc,
  companyStatistics,
  testimonial,
  personName,
  jobPosition,
  asset,
  link,
}) => {
  return (
    <div className={styles.statsTestimonialSection}>
      <Image className={styles.statsTestimonialLogo} alt="" src={companyLogoSrc} />
      {companyStatistics && (
        <div className={styles.statsTestimonialHighlight}>
          <HXL as="h3">{renderRichTextSingleLineContent(companyStatistics)}</HXL>
          <TXL as="div">{renderRichTextMultiLineContent(testimonial)}</TXL>
        </div>
      )}

      <div className={styles.statsTestimonialPerson}>
        <div className={styles.statsTestimonialPersonTitle}>
          <C4 as="div">{personName}</C4>
          <C4 as="div">{jobPosition}</C4>
        </div>
        {asset && <Image alt="" width={64} height={64} src={asset} />}
      </div>
      {link && (
        <div className={styles.statsTestimonialCta} data-link="embeddedLink">
          <C2 as="p">{link.fields.embeddedEntryText}</C2>
          <CtaArrow data-link="embeddedLinkArrow" />
        </div>
      )}
    </div>
  )
}

interface TestimonialItemProps extends ContentProps<TypeItemTestimonial> {
  link?: TypeComponentLink
}

export const TestimonialItem: React.FC<TestimonialItemProps> = (props) => {
  const {
    structure: {
      fields: {
        asset,
        testimonial,
        companyName,
        jobPosition,
        personName,
        layout,
        socialUsername,
        companyLogo,
        companyStatistics,
      },
    },
    link,
  } = props

  const imageSrc = updateAssetCDNUrl(asset?.fields.asset?.fields.file?.url ?? '')

  switch (layout) {
    case 'social_feed':
      return (
        <SocialTestimonialItem
          asset={imageSrc}
          testimonial={testimonial}
          personName={personName}
          socialUsername={socialUsername}
        />
      )
    case 'company_statistics': {
      const companyLogoSrc = updateAssetCDNUrl(companyLogo?.fields.asset?.fields.file?.url ?? '')
      return (
        <StatsTestimonialItem
          companyLogoSrc={companyLogoSrc}
          companyStatistics={companyStatistics}
          asset={imageSrc}
          testimonial={testimonial}
          personName={personName}
          jobPosition={jobPosition}
          link={link}
        />
      )
    }
    case 'default':
    default:
      return (
        <DefaultTestimonialItem
          asset={imageSrc}
          testimonial={testimonial}
          companyName={companyName}
          jobPosition={jobPosition}
          personName={personName}
        />
      )
  }
}
